import { DownOutlined, InfoCircleFilled, UpOutlined } from "@ant-design/icons";
import {
  Card,
  Checkbox,
  Form,
  Image,
  List,
  Modal,
  Tooltip,
  message,
} from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { tip } from "../../../../apis/utils";
import { CustomButton, CustomInput, ProductSpec } from "../../../../components";
import CustomGiftPoint from "../../../../components/CustomGiftPoint";
import { useLogin } from "../../../../context/LoginContext";
import { useShoppingCart } from "../../../../context/ShoppingCartContext";
import { useSupportedDevice } from "../../../../context/SupportedDeviceContext";
import localStorageKey from "../../../../enumerations/localStorageKey";
import { useCheckIfHiddenStore } from "../../../../hooks/HiddenStore/hiddenstore.hooks";
import {
  useCheckDiscount,
  useCheckPromote,
  useCheckout,
  useGetGiftPointRatio,
  useGetProductItem,
} from "../../../../hooks/ShopCart/cart.hooks";
import { usePostEmailSend } from "../../../../hooks/SignUp/signup.hooks";
import {
  useGetGiftPoint,
  useGetUserInfo,
} from "../../../../hooks/UserCenter/usercenter.hooks";
import { moneyFormat } from "../../../../plugins/numberFormat";
import CrossIcon from "../../icons/CrossIcon";
import TickIcon from "../../icons/TickIcon";
import Collapse from "../Collapse";
import styles from "./style.module.scss";

const CartInfoFillout = ({
  selectedProductIds,
}: {
  selectedProductIds: any;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLogin } = useLogin();
  const itemsPerPage = 2;
  const userEmail = window.localStorage.getItem(localStorageKey.USER_ACCOUNT);
  const storeCode = window.localStorage.getItem(localStorageKey.STORE_CODE);
  const nowLang = window.localStorage.getItem(localStorageKey.DEFAULT_LANG);
  const [messageApi, contextHolder] = message.useMessage();
  const [isLoading, setisLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const { setShowModal } = useSupportedDevice();
  const { mutate: checkout } = useCheckout();
  const [displayedItems, setDisplayedItems] = useState<any[]>();
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [checkCoupon, setCheckCoupon] = useState(false);
  const [checkPromo, setCheckPromo] = useState(false);
  const [checkGiftPoint, setCheckGiftPoint] = useState<Boolean>();
  const [orderDetail, setOrderDetail] = useState<any>();
  const [urlParam, setUrlParams] = useState(
    window.localStorage.getItem(localStorageKey.PROMOTE_CODE)
  );
  const [couponDisable, setCouponDisable] = useState(true);
  const [referralDisable, setReferralDisable] = useState(true);
  const [couponHasValue, setCouponHasValue] = useState(true);
  const [referralHasValue, setReferralHasValue] = useState(
    urlParam ? false : true
  );
  const [giftPointDisable, setGiftPointDisable] = useState(true);
  const [giftPointHasValue, setGiftPointHasValue] = useState(true);
  const [giftPointRatio, setGiftPointRato] = useState<any>();
  const [giftPointLimit, setGiftPointLimit] = useState<any>();
  const [isLoadingPromoCode, setIsLoadingPromoCode] = useState(false);
  const [isLoadingCoupon, setIsLoadingCoupon] = useState(false);
  const { data: checkStore, isFetching: checkIsFetching } =
    useCheckIfHiddenStore({ code: storeCode }, { enabled: !!storeCode });
  const { updateCart } = useShoppingCart();
  const { mutate: resendCode } = usePostEmailSend();
  const { data: userInfo } = useGetUserInfo({});
  const { data: giftPoint, refetch } = useGetGiftPoint({});
  const { data: giftPointRate } = useGetGiftPointRatio({});
  const { data: products } = useGetProductItem(
    { code: storeCode, item_id: selectedProductIds },
    {
      enabled:
        !!selectedProductIds && !!((selectedProductIds?.length ?? 0) > 0),
    }
  );

  const payMethodOptions = [
    {
      label: t("normal.creditCard"),
      value: "CREDIT",
      paymentCode: "NEWEBPAY",
    },
    {
      label: t("normal.linepay"),
      value: "LINEPAY",
      paymentCode: "LINEPAY",
    },
  ];

  const [values, setValues] = useState({
    gift_point: 0,
    coupon_code: "",
    referral_code: urlParam ? urlParam : "",
    name: "",
    mobile: "",
    discount_price: 0,
    pay_method: payMethodOptions[0].value,
    payment_code: payMethodOptions[0].paymentCode,
    remark: "",
    order_detail: [],
    total_price: 0,
    full_price: 0,
    sell_price: 0,
    tax_id_number: "",
    company_title: "",
    store_code: "",
  });

  const {
    data: checkDiscount,
    isError: discountFail,
    isSuccess: discountSuccess,
  } = useCheckDiscount(
    {
      discount_code: values?.coupon_code,
      price: values.total_price,
    },
    {
      enabled: checkCoupon,
      onSuccess: () => {
        setIsLoadingCoupon(false);
      },
      onError: (error: any) => {
        tip(error);
        setIsLoadingCoupon(false);
      },
    }
  );
  const {
    data: checkPromote,
    isError: promoFail,
    isSuccess: promoSuccess,
  } = useCheckPromote(
    {
      promote_code: values?.referral_code,
      price: values.total_price,
    },
    {
      enabled: checkPromo,
      onSuccess: () => {
        setIsLoadingPromoCode(false);
      },
      onError: (error: any) => {
        tip(error);
        setIsLoadingPromoCode(false);
      },
    }
  );

  const handleGiftPointChange = (value: any) => {
    handleChange("gift_point")(value);
    setReferralDisable(true);
    setCouponDisable(true);
    setGiftPointHasValue(false);
    setCheckGiftPoint(undefined);
    form.validateFields();
  };

  const handleCouponChange = (value: string) => {
    handleChange("coupon_code")(value);
    setReferralDisable(true);
    setGiftPointDisable(true);
    setCouponHasValue(false);
    form.validateFields();
  };

  const handleReferralChange = (value: any) => {
    handleChange("referral_code")(value);
    setCouponDisable(true);
    setGiftPointDisable(true);
    setReferralHasValue(false);
    form.validateFields();
  };

  const handleCheckGiftPoint = () => {
    if (values.gift_point > giftPoint / Number(giftPointRatio)) {
      setCheckGiftPoint(false);
      return;
    }
    if (giftPointRate?.discount_setting?.requirement === 0) {
      console.log("no Required");
      setCheckGiftPoint(true);
      return true;
    } else {
      const filterGiftPointRate =
        giftPointRate?.discount_setting?.discount_content?.filter((i: any) => {
          return Number(values.total_price) >= Number(i.amount_target);
        });
      const lastEl = filterGiftPointRate[filterGiftPointRate?.length - 1];
      console.log(lastEl);
      if (lastEl !== undefined) {
        const PERCENT_LIMIT =
          (Number(lastEl?.amount_target) *
            (Number(lastEl?.discount_limit) / 100)) /
          giftPointRatio;
        const LIMIT = Number(lastEl?.discount_limit) / giftPointRatio;
        if (giftPointRate?.discount_setting?.discount_type === 1) {
          setGiftPointLimit(PERCENT_LIMIT);
          if (values.gift_point > PERCENT_LIMIT) {
            setCheckGiftPoint(false);
            messageApi.error(t("giftpoint.overLimit.error"));
            return;
          } else {
            setCheckGiftPoint(true);
          }
        } else {
          setGiftPointLimit(LIMIT);
          if (values.gift_point > LIMIT) {
            setCheckGiftPoint(false);
            messageApi.error(t("giftpoint.overLimit.error"));
            return;
          } else {
            setCheckGiftPoint(true);
          }
        }
      } else {
        setCheckGiftPoint(false);
        setGiftPointLimit(null);
        messageApi.error(t("giftpoint.condition.error"));
      }
    }
  };

  const updateCartProcess = () => {
    orderDetail.forEach((item: any) => {
      updateCart(item.id, -item.quantity);
    });
    const cartItemsString: string | null = window.localStorage.getItem(
      localStorageKey.CART
    );
    if (cartItemsString) {
      const cartItems: any[] = JSON.parse(cartItemsString);
      const updatedCart = cartItems.filter(
        (item: any) => item.id !== orderDetail?.id
      );
      window.localStorage.setItem(
        localStorageKey.CART,
        JSON.stringify(updatedCart)
      );
    }
  };
  console.log(checkPromote);
  const onFinish = async () => {
    // setisLoading(true);
    if (userInfo?.status === 0) {
      resendCode();
      navigate("/verify");
      return;
    }
    const checkPromoteDiscountPrice =
      values.coupon_code || values.referral_code || values.gift_point
        ? checkPromote?.discount_price
        : checkPromote?.discount_value;
    const checkDiscountDiscountValue =
      values.coupon_code || values.referral_code || values.gift_point
        ? checkDiscount?.discount_price
        : checkDiscount?.discount_value;
    const field: any = {
      gift_point: values.gift_point * giftPointRatio,
      coupon_code: values.coupon_code,
      referral_code: values.referral_code,
      name: values.name ? values.name : userInfo?.name,
      mobile: values.mobile ? values.mobile : userInfo?.mobile,
      discount_price: checkPromoteDiscountPrice
        ? checkPromoteDiscountPrice
        : checkDiscountDiscountValue
        ? checkDiscountDiscountValue
        : 0,
      pay_method: values?.pay_method,
      payment_code: values?.payment_code,
      remark: "",
      order_detail: orderDetail,
      total_price: values.full_price,
      sell_price: values.store_code
        ? values.total_price
        : checkPromoteDiscountPrice
        ? values.total_price - checkPromoteDiscountPrice
        : checkDiscountDiscountValue
        ? values.total_price - checkDiscountDiscountValue
        : checkGiftPoint
        ? values.total_price - values.gift_point
        : values.total_price,
      tax_id_number: values.tax_id_number,
      company_title: values.company_title,
      store_code: values.store_code,
    };
    if (values.coupon_code || values.referral_code || values.gift_point) {
      if (!discountSuccess && !promoSuccess && !checkGiftPoint) {
        Modal.warning({
          title: t("normal.pleaseConfirm"),
        });
        setisLoading(false);
        return;
      }
    }

    await checkout(
      {
        params: field,
      },
      {
        onSuccess: (res: any) => {
          try {
            if (values.pay_method === payMethodOptions[1].value) {
              window.location.assign(res.data.data.url);
              updateCartProcess();
              return;
            }
            if (res.data.data.type === "payment") {
              const form = document.createElement("form");
              form.method = "post";
              form.action = res.data.data.url;
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = "data";
              input.value = res.data.data.data;
              for (const key in res.data.data.data) {
                if (res.data.data.data.hasOwnProperty(key)) {
                  const input = document.createElement("input");
                  input.type = "hidden";
                  input.name = key;
                  input.value = res.data.data.data[key];
                  form.appendChild(input);
                }
              }
              document.body.appendChild(form);
              form.submit();
              updateCartProcess();
            } else {
              updateCartProcess();
              refetch();
              navigate("/usercenter?tab=orderinquiry");
            }
          } catch (error) {
            console.error("Error", error);
          }
        },
        onError: (error: any) => {
          console.log(error);
          setisLoading(false);
        },
      }
    );
  };

  const loadMore = () => {
    setDisplayedItems(products);
    setShowMore(true);
  };

  const loadLess = () => {
    setDisplayedItems(products?.slice(0, itemsPerPage));
    setShowMore(false);
  };

  //Qty
  const initializeQuantities = (data: any) => {
    const initialQuantities: { [key: number]: number } = {};

    data?.forEach((item: any) => {
      initialQuantities[item.id] = item.quantity;
    });
    setQuantities(initialQuantities);
  };

  const handleChange = (prop: any) => (event: any) => {
    if (prop === "pay_method") {
      const paymentCode = payMethodOptions.filter(
        (item: any) => item.value === event
      )[0]?.paymentCode;
      setValues({
        ...values,
        [prop]: event,
        payment_code: paymentCode,
      });
    } else {
      setValues({ ...values, [prop]: event.target.value });
    }
    setCheckCoupon(false);
    setCheckPromo(false);
  };

  useEffect(() => {
    if (giftPointRate) {
      const GET_RATE = giftPointRate?.ratio?.split(":");
      setGiftPointRato(GET_RATE[1]);
    }
  }, [giftPointRate]);

  useEffect(() => {
    const _local: any = window.localStorage.getItem(localStorageKey.CART);
    const renderData = JSON.parse(_local);
    initializeQuantities(renderData);

    let updatedRenderData = [...renderData];

    updatedRenderData = updatedRenderData.map((item) => {
      const matchingProduct = products?.find((p: any) => p.id === item.id);
      if (matchingProduct) {
        return {
          ...item,
          product_id: matchingProduct.product_id,
        };
      }
      return item;
    });

    const data = updatedRenderData.filter((item) => item.product_id);
    setOrderDetail(data);
    setDisplayedItems(products?.slice(0, 2));
  }, [products]);

  const renderItem = useCallback(
    (item: any) => (
      <List.Item key={item.id} className={styles.cardItems}>
        <div className={styles.card}>
          <Image src={item?.img} style={{ width: "48px" }} preview={false} />
          <div className={styles.content}>
            <div className={styles.contentLeft}>
              <div className={styles.contentMain}>
                {nowLang === "en-US" ? item?.name_en_US : item?.name_zh_TW}
              </div>
              <div className={styles.contentDesc}>
                {ProductSpec(t, item.network_volume, item.type, item.days)}
              </div>
              {item?.hidden_store_code ? (
                <span className={styles.contentPromoteHiddenStore}>
                  {t("normal.promotion.hiddenStore")}
                </span>
              ) : (
                <></>
              )}
              <div className={styles.amount}>
                {t("normal.quantity") + `: ${quantities[item.id]}`}
              </div>
            </div>
            <div className={styles.contentRight}>
              <div className={styles.unitPrice}>{`NT $${moneyFormat(
                item?.store_price
              )}`}</div>
              <div className={styles.total}>{`NT $${moneyFormat(
                quantities[item.id] * item?.store_price
              )}`}</div>
            </div>
          </div>
        </div>
      </List.Item>
    ),
    [quantities]
  );

  useEffect(() => {
    if (
      !storeCode ||
      (checkStore?.discount_type === 0 && checkStore?.discount_value === 0)
    ) {
      setValues((prev) => ({ ...prev, store_code: "" }));
      window.localStorage.removeItem(localStorageKey.STORE_CODE);
    }

    if (storeCode && checkStore) {
      setValues((prev) => ({ ...prev, store_code: storeCode }));
    }
  }, [storeCode, checkStore]);

  useEffect(() => {
    setReferralDisable(!!values.coupon_code || !!values.gift_point);
  }, [values.coupon_code, values.gift_point]);

  useEffect(() => {
    setCouponDisable(
      !!values.referral_code || !!urlParam || !!values.gift_point
    );
    setUrlParams("");
  }, [values.referral_code, values.gift_point, urlParam]);

  useEffect(() => {
    setGiftPointDisable(!!values.coupon_code || !!values.referral_code);
  }, [values.coupon_code, values.referral_code]);

  useEffect(() => {
    let fullPrice = 0;
    let totalPrice = 0;
    products?.forEach((item: any) => {
      fullPrice += quantities[item.id] * item.sell_price;
      totalPrice += quantities[item.id] * item.store_price;
    });

    setValues((prev) => ({
      ...prev,
      full_price: fullPrice,
      total_price: totalPrice,
    }));
  }, [quantities, products]);

  useEffect(() => {
    console.log(checkGiftPoint);
  }, [checkGiftPoint]);

  useEffect(() => {
    if (discountFail || discountSuccess) {
      setIsLoadingCoupon(false);
    }
  }, [discountFail, discountSuccess]);

  useEffect(() => {
    if (promoFail || promoSuccess) {
      setIsLoadingPromoCode(false);
    }
  }, [promoFail, promoSuccess]);

  return (
    <Form
      form={form}
      name="cart"
      autoComplete="off"
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        name: userInfo?.name ? userInfo?.name : "",
        mobile: userInfo?.mobile ? userInfo?.mobile : "",
      }}
    >
      {contextHolder}
      <div className={styles.mainContainer} id="cartInfoFillout">
        <div className={styles.orderInfo}>
          <Card title={t("cart.orderInfo")}>
            {process.env.REACT_APP_PROD_NAME_DEV !== "2esim" ? (
              <>
                <CustomInput
                  formItemProps={{
                    name: "name",
                    label: t("normal.order.user.name"),
                    rules: [
                      {
                        required: true,
                        message: t("form.required", {
                          name: t("normal.order.user.name"),
                        }),
                      },
                      {
                        max: 50,
                        message: t("normal.maxLength", { max: 50 }),
                      },
                    ],
                  }}
                  inputProps={{
                    onChange: handleChange("name"),
                    placeholder: t("signup.enterInput", { content: "" }),
                  }}
                />
                <CustomInput
                  formItemProps={{
                    name: "mobile",
                    label: t("normal.mobile"),
                    rules: [
                      {
                        required: true,
                        message: t("form.required", {
                          name: t("normal.mobile"),
                        }),
                      },
                      {
                        pattern: /^[0-9]*$/,
                        message: t("signup.emailFormat.error"),
                      },
                      {
                        max: 20,
                        message: t("normal.maxLength", { max: 20 }),
                      },
                    ],
                  }}
                  inputProps={{
                    onChange: handleChange("mobile"),
                    placeholder: t("signup.enterInput", { content: "" }),
                  }}
                />
              </>
            ) : (
              ""
            )}
            <CustomInput
              formItemProps={{
                name: "email",
                label: t("normal.email"),
              }}
              inputProps={{
                defaultValue: JSON.parse(JSON.stringify(userEmail)),
                readOnly: true,
                disabled: true,
              }}
            />
          </Card>
          {/* 統一編號 */}
          <div className={styles.uniformNumberSection}>
            <Card title={t("cart.proofOfPurchase")}>
              <CustomInput
                formItemProps={{
                  name: "company_title",
                  label: t("cart.companyTitleAndBuyer"),
                  rules: [
                    {
                      required: !!values?.tax_id_number,
                      message: t("signup.enterInput", {
                        content: t("cart.companyTitleAndBuyer"),
                      }),
                    },
                  ],
                }}
                inputProps={{
                  onChange: handleChange("company_title"),
                  // placeholder: t("signup.enterInput", { content: "" }),
                }}
              />
              <CustomInput
                formItemProps={{
                  name: "tax_id_number",
                  label: t("normal.uniformNumber"),
                  rules: [
                    {
                      pattern: /^[0-9]{8}$/,
                      message: t("uniformNumber.error.hint"),
                    },
                  ],
                }}
                inputProps={{
                  onChange: handleChange("tax_id_number"),
                  // placeholder: t("signup.enterInput", { content: "" }),
                }}
              />
            </Card>
          </div>
          {/* 付款類型 */}
          <div className={styles.bankSection}>
            <Card title={t("cart.paymentType")}>
              <CustomInput
                selectProps={{
                  defaultValue: payMethodOptions[0].value,
                  onChange: handleChange("pay_method"),
                }}
                selectOptions={payMethodOptions}
                formItemProps={{
                  name: "pay_method",
                  label: t("orderInquiry.order.paymentMethod"),
                }}
                formItemType="select"
              />
              {/* <div className={styles.paymentMethod}>
                <VerifyIcon />
                <div className={styles.paymentMethodText}>
                  {t("cart.paymentMethod.desc")}
                </div>
              </div> */}
            </Card>
          </div>
        </div>
        <div className={styles.overview}>
          <Card title={t("cart.orderOverview")}>
            <List
              dataSource={displayedItems}
              renderItem={renderItem}
              footer={
                <>
                  {orderDetail?.length > 2 ? (
                    <>
                      {showMore ? (
                        <div className={styles.showMore} onClick={loadLess}>
                          {t("normal.show.less")} <UpOutlined />
                        </div>
                      ) : (
                        <div className={styles.showMore} onClick={loadMore}>
                          {t("normal.show.more")} <DownOutlined />
                        </div>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                </>
              }
            />
            {/* DISCOUNT */}
            {values.store_code ? (
              <></>
            ) : (
              <>
                {giftPointRate?.status === 0 ||
                process.env.REACT_APP_PROD_NAME_DEV === "2esim" ? (
                  ""
                ) : (
                  <Collapse
                    name="gift_point"
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div>{t("normal.giftpoint")}</div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            color: "var(--grey400)",
                            fontWeight: 500,
                          }}
                        >
                          <CustomGiftPoint number={giftPoint} />
                          {t("giftpoint.approximate", {
                            money: Math.trunc(giftPoint / giftPointRatio),
                          })}
                          <div
                            style={{
                              color: "var(--grey400)",
                              fontWeight: 500,
                              marginRight: "8px",
                            }}
                          >
                            <Tooltip
                              overlayStyle={{ width: "1000px" }}
                              title={
                                <>
                                  {giftPointRate?.discount_setting
                                    .requirement === 1 ? (
                                    <>
                                      <div style={{ marginBottom: "10px" }}>
                                        <div>
                                          {t("giftpoint.discount.threshold")}
                                        </div>
                                        <div>
                                          {t("giftpoint.pointSetting", {
                                            amount:
                                              giftPointRate?.gift_point_setting
                                                .single_consumption,
                                            point:
                                              giftPointRate?.gift_point_setting
                                                .gift_point,
                                            acc:
                                              giftPointRate?.gift_point_setting
                                                .accumulation === 0
                                                ? t("giftpoint.noAccumulation")
                                                : t(
                                                    "giftpoint.hasAccumulation"
                                                  ),
                                          })}
                                        </div>
                                      </div>
                                      <div>{t("giftpoint.discountLimit")}</div>

                                      {giftPointRate?.discount_setting.discount_content.map(
                                        (i: any) => {
                                          return (
                                            <div style={{ textWrap: "nowrap" }}>
                                              {t(
                                                "giftpoint.tooltip.limitation",
                                                {
                                                  target: i.amount_target,
                                                  limit:
                                                    giftPointRate
                                                      ?.discount_setting
                                                      .discount_type === 0
                                                      ? Math.trunc(
                                                          i.discount_limit /
                                                            giftPointRatio
                                                        )
                                                      : Math.trunc(
                                                          (i.amount_target *
                                                            (Number(
                                                              i.discount_limit
                                                            ) /
                                                              100)) /
                                                            giftPointRatio
                                                        ),
                                                }
                                              )}
                                            </div>
                                          );
                                        }
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <div style={{ marginBottom: "10px" }}>
                                        <div>
                                          {t("giftpoint.discount.threshold")}
                                        </div>
                                        <div>
                                          {t("giftpoint.pointSetting", {
                                            amount:
                                              giftPointRate?.gift_point_setting
                                                .single_consumption,
                                            point:
                                              giftPointRate?.gift_point_setting
                                                .gift_point,
                                            acc:
                                              giftPointRate?.gift_point_setting
                                                .accumulation === 0
                                                ? t("giftpoint.noAccumulation")
                                                : t(
                                                    "giftpoint.hasAccumulation"
                                                  ),
                                          })}
                                        </div>
                                      </div>
                                      <div>{t("giftpoint.discountLimit")}</div>
                                      <div>{t("giftpoint.noCondition")}</div>
                                    </>
                                  )}
                                </>
                              }
                            >
                              <InfoCircleFilled
                                style={{
                                  marginLeft: "5px",
                                }}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    }
                    suffix={
                      checkGiftPoint ? (
                        <TickIcon />
                      ) : checkGiftPoint === false ? (
                        <CrossIcon />
                      ) : (
                        ""
                      )
                    }
                    placeholder={t("signup.enterInput", {
                      content: t("normal.cashDiscount"),
                    })}
                    onChange={handleGiftPointChange}
                    disabled={
                      giftPointHasValue ? giftPointHasValue : giftPointDisable
                    }
                    rules={[
                      {
                        pattern: /^[0-9]*$/,
                        message: t("giftpoint.discount.numbersOnly"),
                      },
                      {
                        validator(_: any, value: any, callback: any) {
                          if (value === "") {
                            setGiftPointHasValue(true);
                          } else {
                            setGiftPointHasValue(false);
                          }
                          if (
                            value &&
                            (values.coupon_code || values.referral_code)
                          ) {
                            setGiftPointHasValue(true);
                            callback(t("cart.discount.err.msg"));
                          } else if (
                            value >
                            giftPoint / Number(giftPointRatio)
                          ) {
                            callback(t("giftpoint.discount.error"));
                          } else {
                            callback();
                          }
                        },
                      },
                    ]}
                    onClick={handleCheckGiftPoint}
                  />
                )}
                <Collapse
                  name="coupon_code"
                  label={t("normal.useCoupon")}
                  placeholder={t("signup.enterInput", { content: "" })}
                  onChange={handleCouponChange}
                  loading={isLoadingCoupon}
                  disabled={couponHasValue ? couponHasValue : couponDisable}
                  suffix={
                    discountSuccess ? (
                      <TickIcon />
                    ) : discountFail ? (
                      <CrossIcon />
                    ) : (
                      <span />
                    )
                  }
                  rules={[
                    {
                      validator(_: any, value: any, callback: any) {
                        if (value === "") {
                          setCouponHasValue(true);
                        } else {
                          setCouponHasValue(false);
                        }
                        if (
                          value &&
                          (values.referral_code || values.gift_point)
                        ) {
                          setCouponHasValue(true);
                          callback(t("cart.discount.err.msg"));
                        } else {
                          callback();
                        }
                      },
                    },
                  ]}
                  onClick={() => {
                    setCheckCoupon(true);
                    setIsLoadingCoupon(true);
                  }}
                />
                <Collapse
                  name="referral_code"
                  label={t("normal.usePromoCode")}
                  placeholder={t("signup.enterInput", { content: "" })}
                  onChange={handleReferralChange}
                  loading={isLoadingPromoCode}
                  hasPromote={urlParam ? true : false}
                  defaultValue={urlParam ? urlParam : ""}
                  disabled={
                    referralHasValue ? referralHasValue : referralDisable
                  }
                  suffix={
                    promoSuccess ? (
                      <TickIcon />
                    ) : promoFail ? (
                      <CrossIcon />
                    ) : (
                      <span />
                    )
                  }
                  rules={[
                    {
                      validator(_: any, value: any, callback: any) {
                        if (value === "") {
                          setReferralHasValue(true);
                        } else {
                          setReferralHasValue(false);
                        }
                        if (
                          (value || urlParam) &&
                          (values.coupon_code || values.gift_point)
                        ) {
                          setReferralHasValue(true);
                          callback(t("cart.discount.err.msg"));
                        } else {
                          callback();
                        }
                      },
                    },
                  ]}
                  onClick={() => {
                    setCheckPromo(true);
                    setIsLoadingPromoCode(true);
                  }}
                />
              </>
            )}
            {/* DISCOUNT */}

            <div className={styles.productAmount}>
              <span>{t("normal.product.total.amount")}:</span>
              <div>{`NT $${moneyFormat(values.total_price)}`}</div>
            </div>

            {checkDiscount ? (
              <div className={styles.discountPrice}>
                <span>{t("normal.coupon")}:</span>
                <div className={styles.discountValue}>
                  {checkDiscount?.discount_type === 1 ? (
                    <div className={styles.discountPercent}>
                      {moneyFormat((100 - checkDiscount?.discount_value) / 10) +
                        t("normal.percentOff")}
                    </div>
                  ) : (
                    ""
                  )}
                  {`NT -$${moneyFormat(checkDiscount?.discount_price)}`}
                </div>
              </div>
            ) : (
              ""
            )}

            {checkPromote ? (
              <>
                {checkPromote?.discount_price === 0 ? (
                  ""
                ) : (
                  <div className={styles.discountPrice}>
                    <span>{t("normal.coupon")}:</span>
                    <div className={styles.discountValue}>
                      {checkPromote?.discount_type === 1 ? (
                        <div className={styles.discountPercent}>
                          {moneyFormat(
                            (100 - checkPromote?.discount_value) / 10
                          ) + t("normal.percentOff")}
                        </div>
                      ) : (
                        ""
                      )}
                      {`NT -$${moneyFormat(checkPromote?.discount_price)}`}
                    </div>
                  </div>
                )}
              </>
            ) : (
              ""
            )}
            {checkGiftPoint ? (
              <div className={styles.discountPrice}>
                <span>{t("giftpoint.discount")}:</span>
                <div className={styles.discountValue}>
                  {`NT -$${moneyFormat(values.gift_point)}`}
                </div>
              </div>
            ) : (
              ""
            )}
            <div className={styles.productAmount}>
              <span>{t("normal.total")}:</span>
              <div className={styles.totalPrice}>{`NT $${moneyFormat(
                checkPromote?.discount_value
                  ? values.total_price - checkPromote?.discount_price
                  : checkDiscount?.discount_value
                  ? values.total_price - checkDiscount?.discount_price
                  : checkGiftPoint
                  ? values.total_price - values.gift_point
                  : values.total_price
              )}`}</div>
            </div>
          </Card>
          <div className={styles.Checkbox}>
            <Checkbox required />
            {process.env.REACT_APP_PROD_NAME_DEV === "2esim" ? (
              <div>
                {t("cart.deviceAllowsEsim")}
                <span
                  className={styles.primary}
                  onClick={() => setShowModal(true)}
                >
                  {t("cart.allowedDevice")}
                </span>
                <span>
                  {"，" + t("cart.checkoutDeemedAs")}
                  {t("cart.agreement")}
                  <span
                    className={styles.primary}
                    onClick={() => {
                      window.open("/privacypolicy", "_blank");
                    }}
                  >
                    {" " + t("normal.privacyPolicy")}
                  </span>
                  、
                  <span
                    className={styles.primary}
                    onClick={() => {
                      window.open("/terms", "_blank");
                    }}
                  >
                    {t("normal.termsOfService") + " "}
                  </span>
                  <span>{t("normal.and")}</span>
                  <span
                    className={styles.primary}
                    onClick={() => {
                      window.open("/returnandexchange", "_blank");
                    }}
                  >
                    {" " + t("normal.returnPolicy")}
                  </span>
                </span>
              </div>
            ) : (
              <div>
                {t("cart.agreement")}
                <span
                  className={styles.primary}
                  onClick={() => {
                    window.open("/privacypolicy", "_blank");
                  }}
                >
                  {t("normal.privacyPolicy")}
                </span>
                <span>{t("normal.and")}</span>
                <span
                  className={styles.primary}
                  onClick={() => {
                    window.open("/terms", "_blank");
                  }}
                >
                  {t("normal.termsOfService")}
                </span>
              </div>
            )}
          </div>
          {process.env.REACT_APP_PROD_NAME_DEV === "2esim" ? (
            ""
          ) : (
            <div className={styles.Checkbox}>
              <Checkbox required />
              <div>
                {t("cart.agreement")}
                <span>{t("cart.lockAssurement")}</span>
                <span
                  className={styles.primary}
                  onClick={() => setShowModal(true)}
                >
                  {""} {t("normal.knowMore")}
                </span>
              </div>
            </div>
          )}
          <div className={styles.btn}>
            <CustomButton
              type="primary"
              danger
              htmlType="submit"
              style={{ width: "100%" }}
              loading={isLoading}
              disabled={isLoading}
            >
              {t("normal.pay")}
            </CustomButton>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default CartInfoFillout;
